import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../components/PageShell";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Loader2, Building2, Plus, Trash2, Inbox as InboxIcon, ShieldCheck, MapPin, Phone, Mail, Wand2, Sparkles } from "lucide-react";
import QualificationInput from "../components/QualificationInput";
import { toast } from "sonner";

const EMP_TYPES = ["Full-time", "Part-time", "Contract", "Locum"];
const EXP_LEVELS = ["Any", "Fresher", "1+ years", "2+ years", "3+ years", "5+ years", "10+ years", "Senior"];
const blankRole = () => ({ role_title: "", qualification: "", experience_level: "Any", employment_type: "Full-time", location: "", description: "" });

const AdminPost = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("post");
  const [institutions, setInstitutions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [roles, setRoles] = useState([blankRole()]);
  const [saving, setSaving] = useState(false);
  const [adText, setAdText] = useState("");
  const [parsing, setParsing] = useState(false);
  // add-institution form
  const [inst, setInst] = useState({ name: "", location: "", contact_name: "", contact_phone: "", contact_email: "" });
  const [savingInst, setSavingInst] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) navigate(user ? "/" : "/login");
  }, [authLoading, user, navigate]);

  const loadInstitutions = () => {
    if (user?.role === "admin") api.get("/admin/institutions").then((r) => setInstitutions(r.data)).catch(() => {});
  };
  useEffect(() => { loadInstitutions(); /* eslint-disable-next-line */ }, [user]);

  const setR = (i, k, v) => setRoles((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRole = () => setRoles((rs) => [...rs, blankRole()]);
  const removeRole = (i) => setRoles((rs) => (rs.length === 1 ? rs : rs.filter((_, idx) => idx !== i)));
  const setI = (k) => (e) => setInst({ ...inst, [k]: e.target.value });

  const selected = institutions.find((x) => x.id === selectedId);

  const parseAd = async () => {
    if (adText.trim().length < 10) { toast.error("Paste the recruitment ad text first"); return; }
    setParsing(true);
    try {
      const r = await api.post("/admin/parse-ad", { text: adText });
      const parsed = (r.data.roles || []).map((x) => ({
        role_title: x.role_title || "",
        qualification: x.qualification || "",
        experience_level: EXP_LEVELS.includes(x.experience_level) ? x.experience_level : "Any",
        employment_type: EMP_TYPES.includes(x.employment_type) ? x.employment_type : "Full-time",
        location: x.location || "",
        description: x.description || "",
      }));
      if (parsed.length === 0) { toast.error("No roles detected. Try adding more detail."); return; }
      setRoles(parsed);
      toast.success(`Found ${parsed.length} role(s)`, { description: "Review and edit below, then post." });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not parse the ad");
    } finally {
      setParsing(false);
    }
  };

  const postOpenings = async (e) => {
    e.preventDefault();
    const valid = roles.filter((r) => r.role_title.trim());
    if (!selectedId) { toast.error("Select an institution first"); return; }
    if (valid.length === 0) { toast.error("Add at least one role"); return; }
    setSaving(true);
    try {
      const r = await api.post("/admin/community-openings", { institution_id: selectedId, roles: valid });
      toast.success(`Posted ${r.data.created.length} opening(s)`, { description: `${selected?.name} is now live in the feed.` });
      setRoles([blankRole()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post openings");
    } finally {
      setSaving(false);
    }
  };

  const saveInstitution = async (e) => {
    e.preventDefault();
    if (!inst.name.trim()) { toast.error("Institution name is required"); return; }
    setSavingInst(true);
    try {
      const r = await api.post("/admin/institutions", inst);
      toast.success("Institution saved", { description: "Now select it under Post Openings." });
      setInst({ name: "", location: "", contact_name: "", contact_phone: "", contact_email: "" });
      await api.get("/admin/institutions").then((res) => setInstitutions(res.data));
      setSelectedId(r.data.id);
      setTab("post");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save institution");
    } finally {
      setSavingInst(false);
    }
  };

  if (authLoading || !user || user.role !== "admin")
    return <PageShell><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#1E4FA3]" /></div></PageShell>;

  return (
    <PageShell>
      <div className="mx-auto max-w-[900px] px-6 py-10 lg:px-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#1E4FA3]/10 px-3 py-1 text-xs font-semibold text-[#1E4FA3]"><ShieldCheck className="h-3.5 w-3.5" /> Admin</span>
            <h1 className="mt-3 font-heading text-[32px] font-extrabold text-[#0B2545] sm:text-[38px]">Community Openings</h1>
            <p className="mt-1 text-[16px] text-slate-600">Save institutions once, then post roles by just selecting one.</p>
          </div>
          <Button onClick={() => navigate("/admin/inbox")} variant="outline" className="hidden shrink-0 rounded-xl border-2 border-slate-200 px-4 font-semibold text-slate-700 hover:bg-slate-50 sm:inline-flex"><InboxIcon className="mr-1.5 h-4 w-4" /> Inbox</Button>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList className="mb-5">
            <TabsTrigger value="post">Post Openings</TabsTrigger>
            <TabsTrigger value="institution">Add Institution</TabsTrigger>
          </TabsList>

          {/* ---- Post openings against a saved institution ---- */}
          <TabsContent value="post">
            {institutions.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-12 text-center">
                <p className="text-slate-500">No institutions yet. Add one first.</p>
                <Button onClick={() => setTab("institution")} className="mt-4 h-11 rounded-xl bg-[#1E4FA3] px-6 font-semibold text-white hover:bg-[#193f83]"><Plus className="mr-1.5 h-4 w-4" /> Add Institution</Button>
              </div>
            ) : (
              <form onSubmit={postOpenings} className="space-y-6">
                <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center gap-2"><Building2 className="h-5 w-5 text-[#1E4FA3]" /><h2 className="font-heading text-lg font-bold text-[#0B2545]">Institution</h2></div>
                  <Label>Select institution *</Label>
                  <Select value={selectedId} onValueChange={setSelectedId}>
                    <SelectTrigger className="mt-1.5 h-11"><SelectValue placeholder="Choose a saved institution" /></SelectTrigger>
                    <SelectContent>
                      {institutions.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}{x.location ? ` — ${x.location}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {selected && (
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 rounded-xl bg-slate-50 p-3 text-xs text-slate-500">
                      {selected.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{selected.location}</span>}
                      {selected.contact_phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{selected.contact_phone}</span>}
                      {selected.contact_email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{selected.contact_email}</span>}
                      <span className="font-medium text-amber-600">Private — admin only</span>
                    </div>
                  )}
                  <button type="button" onClick={() => setTab("institution")} className="mt-3 text-sm font-semibold text-[#1E4FA3] hover:underline">+ Add a new institution</button>
                </div>

                <div className="rounded-2xl border border-[#16A34A]/20 bg-[#16A34A]/[0.03] p-6 shadow-sm">
                  <div className="mb-2 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-[#16A34A]" />
                    <h2 className="font-heading text-lg font-bold text-[#0B2545]">Auto-fill from an ad</h2>
                  </div>
                  <p className="mb-3 text-sm text-slate-500">Paste a recruitment advertisement — AI will split it into role rows with qualification and experience prefilled.</p>
                  <Textarea
                    data-testid="ad-text-input"
                    value={adText}
                    onChange={(e) => setAdText(e.target.value)}
                    rows={4}
                    placeholder="e.g. Apollo Hospital Chennai requires: Senior Resident Cardiology (MD Cardiology, 3+ yrs, full time); Staff Nurse ICU (BSc Nursing, freshers, contract)..."
                    className="bg-white"
                  />
                  <Button type="button" onClick={parseAd} disabled={parsing} data-testid="parse-ad-button" className="mt-3 h-11 rounded-xl bg-[#16A34A] px-5 font-semibold text-white hover:bg-[#138a3f]">
                    {parsing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}Auto-fill roles
                  </Button>
                </div>

                <RolesEditor roles={roles} setR={setR} addRole={addRole} removeRole={removeRole} />

                <Button type="submit" disabled={saving} className="h-12 w-full rounded-xl bg-[#1E4FA3] text-[15px] font-semibold text-white hover:bg-[#193f83]">
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Post {roles.filter((r) => r.role_title.trim()).length || ""} Opening(s)
                </Button>
              </form>
            )}
          </TabsContent>

          {/* ---- Add institution ---- */}
          <TabsContent value="institution">
            <form onSubmit={saveInstitution} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-2"><Building2 className="h-5 w-5 text-[#1E4FA3]" /><h2 className="font-heading text-lg font-bold text-[#0B2545]">New Institution</h2></div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="iname">Institution name *</Label>
                  <Input id="iname" required value={inst.name} onChange={setI("name")} className="mt-1.5 h-11" placeholder="e.g. Apollo Hospital" />
                </div>
                <div>
                  <Label htmlFor="iloc">Location</Label>
                  <Input id="iloc" value={inst.location} onChange={setI("location")} className="mt-1.5 h-11" placeholder="e.g. Chennai, India" />
                </div>
              </div>
              <div className="mt-4 rounded-xl bg-slate-50 p-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Private contact (visible to admin only)</p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div><Label htmlFor="cn">Contact name</Label><Input id="cn" value={inst.contact_name} onChange={setI("contact_name")} className="mt-1.5 h-11" placeholder="HR Desk" /></div>
                  <div><Label htmlFor="cp">Phone</Label><Input id="cp" value={inst.contact_phone} onChange={setI("contact_phone")} className="mt-1.5 h-11" placeholder="+91 ..." /></div>
                  <div><Label htmlFor="ce">Email</Label><Input id="ce" value={inst.contact_email} onChange={setI("contact_email")} className="mt-1.5 h-11" placeholder="hr@institution.in" /></div>
                </div>
              </div>
              <Button type="submit" disabled={savingInst} className="mt-5 h-11 w-full rounded-xl bg-[#16A34A] text-[15px] font-semibold text-white hover:bg-[#138a3f]">
                {savingInst && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Institution
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </PageShell>
  );
};

const RolesEditor = ({ roles, setR, addRole, removeRole }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
    <div className="mb-4 flex items-center justify-between">
      <h2 className="font-heading text-lg font-bold text-[#0B2545]">Roles ({roles.length})</h2>
      <Button type="button" onClick={addRole} variant="outline" className="h-9 rounded-lg border-2 border-[#16A34A]/30 px-3 text-sm font-semibold text-[#16A34A] hover:bg-[#16A34A]/5"><Plus className="mr-1 h-4 w-4" /> Add role</Button>
    </div>
    <div className="space-y-4">
      {roles.map((r, i) => (
        <div key={i} className="rounded-xl border border-slate-100 p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-500">Role {i + 1}</span>
            {roles.length > 1 && (
              <button type="button" onClick={() => removeRole(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Role title *</Label><Input value={r.role_title} onChange={(e) => setR(i, "role_title", e.target.value)} className="mt-1.5 h-11" placeholder="e.g. Professor - Cardiology" /></div>
            <div><Label>Qualification needed</Label><QualificationInput listId="role-qual-options" value={r.qualification} onChange={(e) => setR(i, "qualification", e.target.value)} className="mt-1.5 h-11" placeholder="e.g. MD Cardiology, BSc Nursing" /></div>
            <div>
              <Label>Experience</Label>
              <Select value={r.experience_level} onValueChange={(v) => setR(i, "experience_level", v)}>
                <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{EXP_LEVELS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Employment type</Label>
              <Select value={r.employment_type} onValueChange={(v) => setR(i, "employment_type", v)}>
                <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{EMP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Location (optional)</Label><Input value={r.location} onChange={(e) => setR(i, "location", e.target.value)} className="mt-1.5 h-11" placeholder="defaults to institution" /></div>
          </div>
          <div className="mt-3"><Label>Description</Label><Textarea value={r.description} onChange={(e) => setR(i, "description", e.target.value)} rows={2} className="mt-1.5" placeholder="Short role description..." /></div>
        </div>
      ))}
    </div>
  </div>
);

export default AdminPost;
